import { Component, OnInit, inject, Pipe, PipeTransform } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChemicalService, Chemical } from '../../../../core/services/chemical.service';
import { HowToBuyModalComponent } from '../../../../shared/how-to-buy-modal/how-to-buy-modal.component';
import { environment } from 'src/environments/environment';
import { PhoneFormatPipe } from '../../../../shared/pipes';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

@Pipe({
  name: 'highlight',
  standalone: true
})
export class HighlightPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(text: string | null | undefined, search: string): SafeHtml {
    if (!text || !search || search.length < 2) {
      return text || '';
    }

    // Escape special regex characters in search term
    const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearch})`, 'gi');
    const highlighted = text.replace(regex, '<mark class="search-highlight">$1</mark>');

    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }
}

@Component({
  selector: 'app-chemical-book',
  standalone: true,
  imports: [CommonModule, FormsModule, HighlightPipe, HowToBuyModalComponent, PhoneFormatPipe, MaxLengthDirective],
  templateUrl: './chemical-book.component.html',
  styleUrls: ['./chemical-book.component.scss']
})
export class ChemicalBookComponent implements OnInit {
  private chemicalService = inject(ChemicalService);
  private router = inject(Router);

  // Service signals
  isLoading = this.chemicalService.isLoading;
  isSearching = this.chemicalService.isSearching;
  error = this.chemicalService.error;
  epaCount = this.chemicalService.epaCount;
  adnCount = this.chemicalService.adnCount;
  total = this.chemicalService.total;
  currentPage = this.chemicalService.currentPage;
  totalPages = this.chemicalService.totalPages;

  // Local state (non-signal for template compatibility)
  searchQuery = '';
  selectedCategory = '';
  activeTab: 'epa' | 'adn-recommended' = 'epa';
  pageSize = 50; // Items per page

  // Reference guide
  isReferenceGuideOpen = false;

  // How to Buy modal state
  showHowToBuyModal = false;
  selectedSponsorId: string | null = null;

  // Chemical details modal state
  showDetailsModal = false;
  selectedChemical: Chemical | null = null;

  // Categories
  categories = [
    { value: '', label: 'All Categories' },
    { value: 'herbicide', label: 'Herbicides' },
    { value: 'insecticide', label: 'Insecticides' },
    { value: 'fungicide', label: 'Fungicides' },
    { value: 'fertilizer', label: 'Fertilizers' },
    { value: 'adjuvant', label: 'Adjuvants' },
    { value: 'igr', label: 'IGR (Insect Growth Regulator)' },
    { value: 'miticide', label: 'Miticides' },
    { value: 'pgr', label: 'PGR (Plant Growth Regulator)' },
    { value: 'other', label: 'Other' }
  ];

  // All chemicals from current search (already filtered by source on backend)
  get filteredChemicals(): Chemical[] {
    return this.chemicalService.searchResults();
  }

  // Featured ADN-recommended chemicals
  get featuredChemicals(): Chemical[] {
    return this.chemicalService.searchResults().filter(c => c.isFeatured);
  }

  ngOnInit(): void {
    this.loadChemicals();
  }

  async loadChemicals(page: number = 1): Promise<void> {
    await this.chemicalService.searchChemicals('', {
      limit: this.pageSize,
      page,
      source: this.activeTab
    });
  }

  async onSearchChange(): Promise<void> {
    await this.chemicalService.searchChemicals(this.searchQuery, {
      category: this.selectedCategory || undefined,
      source: this.activeTab,
      limit: this.pageSize,
      page: 1 // Reset to first page on search
    });
  }

  async onCategoryChange(): Promise<void> {
    await this.onSearchChange();
  }

  async setActiveTab(tab: 'epa' | 'adn-recommended'): Promise<void> {
    this.activeTab = tab;
    // Reset search/filters when switching tabs
    this.searchQuery = '';
    this.selectedCategory = '';
    await this.loadChemicals(1);
  }

  // Pagination methods
  async goToPage(page: number): Promise<void> {
    if (page < 1 || page > this.totalPages()) return;
    await this.chemicalService.searchChemicals(this.searchQuery, {
      category: this.selectedCategory || undefined,
      source: this.activeTab,
      limit: this.pageSize,
      page
    });
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async previousPage(): Promise<void> {
    await this.goToPage(this.currentPage() - 1);
  }

  async nextPage(): Promise<void> {
    await this.goToPage(this.currentPage() + 1);
  }

  // Get array of page numbers for pagination display
  getPageNumbers(): number[] {
    const current = this.currentPage();
    const total = this.totalPages();
    const pages: number[] = [];

    // Show up to 5 pages centered around current
    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + 4);

    // Adjust start if we're near the end
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  viewLabel(chemical: Chemical, url?: string): void {
    const labelUrl = url
      || chemical.currentLabelUrl
      || (chemical.currentLabels && chemical.currentLabels.length > 0 ? chemical.currentLabels[0].url : null);
    if (labelUrl) {
      try {
        const parsed = new URL(labelUrl);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
      } catch {
        return;
      }
      window.open(labelUrl, '_blank', 'noopener,noreferrer');
    }
  }

  getCategoryBadgeClass(category: string): string {
    switch (category) {
      case 'herbicide':
        return 'badge-herbicide';
      case 'insecticide':
        return 'badge-insecticide';
      case 'fungicide':
        return 'badge-fungicide';
      case 'fertilizer':
        return 'badge-fertilizer';
      case 'adjuvant':
        return 'badge-adjuvant';
      case 'igr':
        return 'badge-igr';
      case 'miticide':
        return 'badge-miticide';
      case 'pgr':
        return 'badge-pgr';
      case 'other':
        return 'badge-other';
      default:
        return 'bg-secondary';
    }
  }

  getCategoryLabel(category: string): string {
    const found = this.categories.find((c: { value: string; label: string }) => c.value === category);
    return found?.label || category?.charAt(0).toUpperCase() + category?.slice(1) || 'Other';
  }

  getSignalWordClass(signalWord: string): string {
    switch (signalWord) {
      case 'CAUTION':
        return 'signal-caution';
      case 'WARNING':
        return 'signal-warning';
      case 'DANGER':
        return 'signal-danger';
      default:
        return '';
    }
  }

  // Get display name for chemical (uses primary custom name if available)
  getDisplayName(chemical: Chemical): string {
    return chemical.displayName || chemical.name;
  }

  // Get a preview of alternate brand names (truncated if too long)
  getAltNamesPreview(altNames: string | null | undefined): string {
    if (!altNames) return '';
    // Split by semicolon and show first 2-3 names
    const names = altNames.split(';').map(n => n.trim()).filter(n => n);
    if (names.length <= 2) {
      return names.join(', ');
    }
    return `${names.slice(0, 2).join(', ')} (+${names.length - 2} more)`;
  }

  // Product Details — opens modal which fetches contact data from API.
  // Modal shows locations/reps when available, website link when contact_form_only.
  openProductDetails(chemical: Chemical): void {
    this.selectedSponsorId = chemical.sponsorId || null;
    this.showHowToBuyModal = true;
  }

  closeHowToBuyModal(): void {
    this.showHowToBuyModal = false;
    this.selectedSponsorId = null;
  }

  visitSponsor(chemical: Chemical): void {
    if (chemical.sponsorWebsite) {
      window.open(chemical.sponsorWebsite, '_blank');
    }
  }

  // Chemical details modal methods
  openDetailsModal(chemical: Chemical): void {
    this.selectedChemical = chemical;
    this.showDetailsModal = true;
  }

  closeDetailsModal(): void {
    this.showDetailsModal = false;
    this.selectedChemical = null;
  }

  // Format array fields for display
  formatArrayField(value: string[] | null | undefined): string {
    if (!value || value.length === 0) return '—';
    return value.join(', ');
  }

  // Resolve logo URL (prepend CDN for relative S3 keys)
  getLogoFullUrl(logoUrl: string | null | undefined): string {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http')) return logoUrl;
    return `${environment.appCDN}/${logoUrl}`;
  }
}
